import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { mockDestinations } from '@/mocks/travel';

gsap.registerPlugin(ScrollTrigger);

export default function DestinationsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const cards = sectionRef.current?.querySelectorAll('.dest-card');
      if (cards) {
        gsap.fromTo(
          cards,
          { y: 60, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.8,
            stagger: 0.1,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: sectionRef.current,
              start: 'top 70%',
              toggleActions: 'play none none reverse',
            },
          }
        );
      }
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section id="destinos" ref={sectionRef} className="relative w-full py-24 md:py-32 px-6 md:px-10 lg:px-16 bg-background-50">
      <div className="max-w-[1440px] mx-auto">
        <div className="text-center mb-16 md:mb-20">
          <span className="inline-block text-xs tracking-[0.2em] uppercase text-primary-500 font-label mb-4">
            Destinos
          </span>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-foreground-900 leading-tight">
            O mundo é o seu <span className="italic">destino</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {mockDestinations.map((dest) => (
            <a
              key={dest.id}
              href="#"
              className="dest-card group relative block aspect-[4/5] overflow-hidden rounded-lg cursor-pointer"
            >
              <img
                src={dest.image}
                alt={dest.name}
                className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h3 className="font-heading text-2xl md:text-3xl font-medium text-white mb-1">
                  {dest.name}
                </h3>
                <span className="text-white/70 text-sm font-light">
                  {dest.count} destinos disponíveis
                </span>
              </div>
            </a>
          ))}
        </div>

        {/* Featured row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mt-4 md:mt-6">
          <a
            href="#"
            className="dest-card group relative block aspect-[16/9] overflow-hidden rounded-lg cursor-pointer"
          >
            <img
              src="https://readdy.ai/api/search-image?query=Cinematic%20wide%20shot%20of%20a%20luxury%20overwater%20villa%20in%20Maldives%20at%20sunset%20with%20crystal%20clear%20turquoise%20water%2C%20palm%20trees%2C%20paradise%20aesthetic%2C%20ARRI%20Alexa%20style%2C%204K%20quality%2C%20ultimate%20luxury%20travel&width=1200&height=675&seq=dest-feat-1&orientation=landscape"
              alt="Experiências exclusivas"
              className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <span className="text-accent-400 text-xs tracking-[0.2em] uppercase font-label mb-2 block">
                Em Destaque
              </span>
              <h3 className="font-heading text-3xl md:text-4xl font-medium text-white">
                Experiências Exclusivas
              </h3>
            </div>
          </a>
          <a
            href="#"
            className="dest-card group relative block aspect-[16/9] overflow-hidden rounded-lg cursor-pointer"
          >
            <img
              src="https://readdy.ai/api/search-image?query=Cinematic%20wide%20shot%20of%20a%20student%20studying%20at%20a%20beautiful%20university%20campus%20with%20historic%20architecture%20and%20autumn%20colors%2C%20exchange%20program%20aesthetic%2C%20ARRI%20Alexa%20style%2C%20warm%20lighting%2C%204K%20quality&width=1200&height=675&seq=dest-feat-2&orientation=landscape"
              alt="Intercâmbio"
              className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <span className="text-secondary-400 text-xs tracking-[0.2em] uppercase font-label mb-2 block">
                Intercâmbio
              </span>
              <h3 className="font-heading text-3xl md:text-4xl font-medium text-white">
                Estude ao Redor do Mundo
              </h3>
            </div>
          </a>
        </div>
      </div>
    </section>
  );
}